import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { auth, storage } from '../firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const tierLimits = {
  founding: { preroll: 1, banner: 1, logo: 1, additional: 1 },
  community: { preroll: 2, banner: 2, logo: 1, additional: 3 },
  category: { preroll: 3, banner: 3, logo: 1, additional: 5 },
  presenting: { preroll: 99, banner: 99, logo: 2, additional: 99 },
};

const tierPerks = {
  founding: [
    'Film Pre-Roll on 1 film',
    'Logo on site and in closing credits',
    'Banner ad during festival',
    'Post-festival performance report',
  ],
  community: [
    'Everything in Founding, plus:',
    'Film Pre-Rolls on 5 films',
    'Ad placement during 2 Q&A sessions',
    'Ads shown during post-festival replays',
    'Pause ad overlay',
    'Sponsored break-out session mention',
    'Social media posts',
    'Film DNA audience data report',
  ],
  category: [
    'Everything in Community, plus:',
    'Film Pre-Rolls on 15+ films across your category',
    'Ad placement during 10 Q&A sessions',
    'Ads shown during all post-festival replays',
    '"Best [Category] presented by [Brand]"',
    '17 categories available',
    'Group buying units across your category screenings',
    'Named break-out session sponsorship',
    'Live shoutouts in Q&As',
    'Logo on award certificates',
  ],
  presenting: [
    'Everything in Category, plus:',
    'Film Pre-Rolls on ALL films',
    'Ad placement during ALL Q&A sessions',
    'Ads shown during ALL replays for the full 7 days',
    '"Synema Interactive Film Festival, Presented by [Your Brand]"',
    'Opening and closing ceremony branding',
    'Dedicated sponsor spotlight segment',
    'All award categories co-branded',
    'Keynote or guest speaker slot',
    'Year-round banner on SynemaFest.com',
    'Custom Film DNA audience report',
    'Dedicated account manager',
  ],
};

const uploadCategories = [
  { key: 'preroll', label: 'Pre-Roll Videos', accept: 'video/mp4,video/quicktime', desc: 'Your ad spot — plays before screenings. MP4 or MOV, max 100MB.' },
  { key: 'banner', label: 'Banner Ads', accept: 'image/png,image/jpeg,image/webp', desc: 'Displayed in screening rooms. 16:9 ratio recommended. PNG or JPG.' },
  { key: 'logo', label: 'Company Logo', accept: 'image/png,image/svg+xml', desc: 'For site placement, credits, and certificates. PNG or SVG, transparent background.' },
  { key: 'additional', label: 'Additional Assets', accept: 'image/*,video/*,application/pdf', desc: 'Any other promotional materials — images, videos, PDFs.' },
];

const SponsorDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!user) return;
    setDashLoading(true);
    setDashError(null);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/.netlify/functions/get-sponsor-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setDashData(data);
    } catch (err) {
      setDashError(err.message || 'Failed to load dashboard');
    } finally {
      setDashLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) loadDashboard();
  }, [user, loadDashboard]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch {
      setLoginError('Invalid email or password.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleUpload = async (category, file) => {
    if (!dashData || !user) return;

    const limits = tierLimits[dashData.tier] || tierLimits.founding;
    const currentCount = (dashData.assets?.[category] || []).length;
    const max = limits[category];

    if (currentCount >= max) {
      alert(`You've reached the maximum of ${max === 99 ? 'unlimited' : max} ${category} upload${max !== 1 ? 's' : ''} for your tier.`);
      return;
    }

    const uploadKey = `${category}_${Date.now()}`;
    setUploading((prev) => ({ ...prev, [uploadKey]: true }));

    try {
      const filePath = `sponsors/${user.uid}/${category}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress((prev) => ({ ...prev, [uploadKey]: progress }));
        },
        (error) => {
          console.error('Upload error:', error);
          alert('Upload failed. Please try again.');
          setUploading((prev) => ({ ...prev, [uploadKey]: false }));
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Save to Firestore via serverless function
          const idToken = await user.getIdToken();
          await fetch('/.netlify/functions/save-sponsor-asset', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              idToken,
              category,
              fileName: file.name,
              fileUrl: downloadURL,
              filePath,
              fileType: file.type,
              fileSize: file.size,
            }),
          });

          setUploading((prev) => ({ ...prev, [uploadKey]: false }));
          loadDashboard(); // Refresh data
        }
      );
    } catch (err) {
      alert('Upload failed: ' + err.message);
      setUploading((prev) => ({ ...prev, [uploadKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <div className="text-muted text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-gold mb-4 text-center">
              Sponsor Dashboard
            </div>
            <h1 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-[0.04em] leading-none text-white mb-8 text-center">
              Log in to your account.
            </h1>
            <form onSubmit={handleLogin} className="space-y-5">
              {loginError && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400">{loginError}</div>
              )}
              <div>
                <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">Email</label>
                <input type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-gold/40 focus:outline-none transition-colors"
                  placeholder="Your sponsor email" />
              </div>
              <div>
                <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">Password</label>
                <input type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-gold/40 focus:outline-none transition-colors"
                  placeholder="Your password" />
              </div>
              <button type="submit" disabled={loginLoading}
                className={`w-full bg-gold text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3.5 rounded-sm transition-all ${loginLoading ? 'opacity-60' : 'hover:opacity-90'}`}>
                {loginLoading ? 'Logging in...' : 'Log In'}
              </button>
            </form>
            <p className="text-center mt-6 text-[0.8rem] text-muted">
              Don't see our email? Check your spam folder for a message from synemafest@gmail.com.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex justify-between items-start mb-12">
            <div>
              <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-gold mb-4">
                Sponsor Dashboard
              </div>
              <h1 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-[0.04em] leading-none text-white">
                {dashData ? `Welcome, ${dashData.company || dashData.contactName}.` : 'Loading...'}
              </h1>
            </div>
            <button onClick={() => { signOut(auth); setDashData(null); }}
              className="text-[0.78rem] text-muted hover:text-white transition-colors mt-2">
              Log out
            </button>
          </div>

          {dashLoading && <div className="text-center py-20 text-muted">Loading dashboard...</div>}
          {dashError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded p-4 mb-8 text-[0.85rem] text-red-400">{dashError}</div>
          )}

          {dashData && (
            <>
              {/* Tier info */}
              <div className="bg-dark2 border border-gold/20 rounded p-6 mb-8">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-gold">
                      {dashData.tierName} Sponsor
                    </span>
                    <div className="font-display text-3xl text-gold mt-1">${(dashData.amountPaid / 100).toFixed(0)}</div>
                  </div>
                  <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-1 rounded ${
                    dashData.assetStatus === 'approved' ? 'bg-teal/10 text-teal' :
                    dashData.assetStatus === 'in_review' ? 'bg-orange/10 text-orange' :
                    'bg-white/[0.05] text-muted'
                  }`}>
                    {dashData.assetStatus === 'approved' ? 'Assets Approved' :
                     dashData.assetStatus === 'in_review' ? 'In Review' :
                     'Awaiting Uploads'}
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-[0.7rem] font-mono tracking-wider uppercase text-muted mb-2">Your package includes</div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {(tierPerks[dashData.tier] || []).map((perk, i) => (
                      <li key={i} className="flex gap-2 items-start text-[0.8rem] font-light text-white/50">
                        <span className="w-1 h-1 rounded-full bg-gold flex-shrink-0 mt-2" />
                        {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Upload sections */}
              <div className="space-y-6 mb-12">
                <h2 className="font-display text-xl text-white">UPLOAD YOUR ASSETS</h2>
                {uploadCategories.map((cat) => {
                  const limits = tierLimits[dashData.tier] || tierLimits.founding;
                  const max = limits[cat.key];
                  const currentAssets = dashData.assets?.[cat.key] || [];
                  const isUnlimited = max >= 99;
                  const canUpload = currentAssets.length < max;

                  return (
                    <div key={cat.key} className="bg-dark2 border border-white/[0.06] rounded p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-display text-lg text-white">{cat.label.toUpperCase()}</h3>
                          <p className="text-[0.78rem] text-muted mt-1">{cat.desc}</p>
                        </div>
                        <span className="font-mono text-[0.65rem] text-muted">
                          {currentAssets.length}/{isUnlimited ? '∞' : max}
                        </span>
                      </div>

                      {/* Existing uploads */}
                      {currentAssets.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {currentAssets.map((asset, i) => (
                            <div key={i} className="flex justify-between items-center bg-dark3 rounded px-4 py-2.5">
                              <div className="flex items-center gap-3">
                                <span className="text-[0.8rem] text-white/70">{asset.fileName}</span>
                                <span className="text-[0.65rem] text-muted">
                                  {(asset.fileSize / (1024 * 1024)).toFixed(1)}MB
                                </span>
                              </div>
                              <span className={`text-[0.6rem] uppercase tracking-wider ${
                                asset.status === 'approved' ? 'text-teal' :
                                asset.status === 'revision' ? 'text-orange' :
                                'text-muted'
                              }`}>
                                {asset.status === 'approved' ? 'Approved' :
                                 asset.status === 'revision' ? 'Needs Revision' :
                                 'Pending Review'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Upload button */}
                      {canUpload ? (
                        <label className="flex items-center justify-center w-full border-2 border-dashed border-white/[0.08] hover:border-gold/30 rounded p-6 cursor-pointer transition-colors group">
                          <input
                            type="file"
                            accept={cat.accept}
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files[0]) handleUpload(cat.key, e.target.files[0]);
                              e.target.value = '';
                            }}
                          />
                          {Object.values(uploading).some(Boolean) ? (
                            <span className="text-[0.8rem] text-muted">
                              Uploading... {Object.values(uploadProgress).filter(Boolean)[0] || 0}%
                            </span>
                          ) : (
                            <span className="text-[0.8rem] text-muted group-hover:text-gold transition-colors">
                              Click to upload or drag and drop
                            </span>
                          )}
                        </label>
                      ) : (
                        <p className="text-[0.75rem] text-muted text-center py-3">
                          {isUnlimited ? '' : `Maximum ${max} upload${max !== 1 ? 's' : ''} reached for your tier.`}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Contact */}
              <div className="bg-dark2 border border-white/[0.06] rounded p-6">
                <h3 className="font-display text-lg text-white mb-3">NEED HELP?</h3>
                <p className="text-[0.85rem] font-light text-white/50 leading-relaxed">
                  For questions about your sponsorship, asset specifications, or campaign strategy, contact your dedicated Synema Film Fest rep at{' '}
                  <a href="mailto:synemafest@gmail.com" className="text-gold font-medium">synemafest@gmail.com</a>
                </p>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default SponsorDashboard;
